import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Calendar, MapPin, Video, CheckCircle2, XCircle, Clock,
  ExternalLink, AlertCircle, Download, Plus, ArrowRightLeft, Loader2, X, MessageSquare,
} from "lucide-react";
import { API, portalHeaders } from "../helpers.js";

const TYPE_LABELS = { in_person: "En personne", online: "En ligne" };
const STATUS_BADGES = {
  pending:   { label: "Action requise",  cls: "bg-amber-100 text-amber-700",   Icon: Clock },
  confirmed: { label: "Confirme",        cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  declined:  { label: "Refuse",          cls: "bg-red-100 text-red-700",        Icon: XCircle },
  cancelled: { label: "Annule",          cls: "bg-gray-100 text-gray-500",      Icon: XCircle },
  completed: { label: "Termine",         cls: "bg-blue-100 text-blue-700",      Icon: CheckCircle2 },
  counter_proposed: { label: "Contre-propose", cls: "bg-violet-100 text-violet-700", Icon: ArrowRightLeft },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AppointmentsTab({ primaryColor }) {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [counterFor, setCounterFor] = useState(null); // id du RDV qu'on contre-propose

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/portal/appointments`, { headers: portalHeaders() });
      setAppts(r.data);
    } catch {
      // silencieux — affiche etat vide
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (id, status) => {
    if (!window.confirm(status === "confirmed" ? "Confirmer ce rendez-vous ?" : "Refuser ce rendez-vous ?")) return;
    setResponding(id);
    try {
      await axios.post(`${API}/portal/appointments/${id}/respond`,
        { status, notes_client: "" },
        { headers: portalHeaders() }
      );
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally {
      setResponding(null);
    }
  };

  const submitCounter = async (appt, newStart, durationMin, note) => {
    setResponding(appt.id);
    try {
      await axios.post(`${API}/portal/appointments/${appt.id}/counter-propose`,
        { new_start_time: newStart, duration_minutes: parseInt(durationMin, 10) || 30, notes_client: note || "" },
        { headers: portalHeaders() }
      );
      setCounterFor(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally {
      setResponding(null);
    }
  };

  const submitRequest = async (form) => {
    setResponding("request");
    try {
      const startISO = `${form.preferred_date}T${form.preferred_time}:00`;
      await axios.post(`${API}/portal/appointments/request`, {
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        preferred_start_time: startISO,
        duration_minutes: parseInt(form.duration_minutes, 10) || 30,
        notes_client: form.notes_client.trim(),
      }, { headers: portalHeaders() });
      setShowRequestForm(false);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally {
      setResponding(null);
    }
  };

  if (loading) {
    return <div className="text-center text-gray-400 py-8">Chargement...</div>;
  }

  const upcoming = appts.filter(a => new Date(a.start_time) >= new Date() && a.status !== "cancelled");
  const past = appts.filter(a => new Date(a.start_time) < new Date() || a.status === "cancelled");

  return (
    <div className="space-y-6" data-testid="appt-portal-tab">
      {/* Header avec bouton Demander un RDV */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold flex items-center gap-2" style={{ color: primaryColor }}>
          <Calendar className="w-5 h-5" /> Rendez-vous
        </h2>
        <button
          onClick={() => setShowRequestForm(true)}
          className="px-3 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5 shadow-sm"
          style={{ backgroundColor: primaryColor }}
          data-testid="appt-portal-request-btn"
        >
          <Plus className="w-4 h-4" /> Demander un RDV
        </button>
      </div>

      {showRequestForm && (
        <RequestForm
          primaryColor={primaryColor}
          busy={responding === "request"}
          onClose={() => setShowRequestForm(false)}
          onSubmit={submitRequest}
        />
      )}

      {appts.length === 0 ? (
        <div className="bg-white border rounded-2xl p-10 text-center" data-testid="appt-portal-empty">
          <Calendar className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700">Aucun rendez-vous</h3>
          <p className="text-sm text-gray-500 mt-1">
            Cliquez « Demander un RDV » pour planifier une rencontre avec votre comptable.
          </p>
        </div>
      ) : (
        <>
          <div>
            {upcoming.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Aucun rendez-vous à venir.</p>
            ) : (
              <div className="space-y-2">
                {upcoming.map(a => (
                  <div key={a.id}>
                    <ApptCard appt={a} primaryColor={primaryColor}
                      onRespond={respond}
                      onOpenCounter={() => setCounterFor(a.id)}
                      responding={responding === a.id} />
                    {counterFor === a.id && (
                      <CounterForm
                        primaryColor={primaryColor}
                        appt={a}
                        busy={responding === a.id}
                        onClose={() => setCounterFor(null)}
                        onSubmit={(s, d, n) => submitCounter(a, s, d, n)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {past.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Historique</h3>
              <div className="space-y-2 opacity-60">
                {past.slice(0, 10).map(a => (
                  <ApptCard key={a.id} appt={a} primaryColor={primaryColor}
                    onRespond={respond} responding={false} muted />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ApptCard({ appt, primaryColor, onRespond, onOpenCounter, responding, muted = false }) {
  const badge = STATUS_BADGES[appt.status] || STATUS_BADGES.pending;
  const TypeIcon = appt.type === "online" ? Video : MapPin;
  // Le client peut agir uniquement sur les RDV pending ENVOYÉS PAR L'ADMIN
  // (source !== "client_request"). Sur ses propres demandes, il attend la réponse de l'admin.
  const isAdminRequest = appt.source !== "client_request";
  const canRespond = !muted && (appt.status === "pending" || appt.status === "counter_proposed") && isAdminRequest;

  return (
    <div className="bg-white border rounded-xl p-4" data-testid={`appt-card-${appt.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${badge.cls}`}>
              <badge.Icon className="w-3 h-3" /> {badge.label}
            </span>
            <span className="text-base font-semibold text-gray-800">{appt.title}</span>
            {!isAdminRequest && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Ma demande</span>
            )}
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-2 flex-wrap">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            {fmtDate(appt.start_time)}
          </div>
          <div className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1.5">
            <TypeIcon className="w-3 h-3" /> {TYPE_LABELS[appt.type]}
            {appt.location && <span className="ml-1">— {appt.location}</span>}
          </div>
          {appt.meeting_url && (
            <a href={appt.meeting_url} target="_blank" rel="noreferrer"
              className="text-xs hover:underline mt-1.5 inline-flex items-center gap-1"
              style={{ color: primaryColor }}>
              <ExternalLink className="w-3 h-3" /> Rejoindre la reunion
            </a>
          )}
          {appt.description && (
            <p className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{appt.description}</p>
          )}
          {/* Iter81 — telechargement .ics */}
          <button onClick={async () => {
            try {
              const r = await axios.get(`${API}/portal/appointments/${appt.id}/ics`, { headers: portalHeaders(), responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a"); a.href = url; a.download = `rdv-${appt.id.slice(0, 8)}.ics`;
              document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
            } catch { alert("Echec du telechargement"); }
          }} className="text-xs mt-1.5 inline-flex items-center gap-1 text-gray-500 hover:text-gray-700"
            data-testid={`appt-ics-${appt.id}`}>
            <Download className="w-3 h-3" /> Ajouter au calendrier (.ics)
          </button>
        </div>

        {canRespond && (
          <div className="flex flex-col gap-1.5">
            <button onClick={() => onRespond(appt.id, "confirmed")} disabled={responding}
              className="px-3 py-1.5 rounded-lg text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid={`appt-confirm-${appt.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Confirmer
            </button>
            {onOpenCounter && (
              <button onClick={onOpenCounter} disabled={responding}
                className="px-3 py-1.5 rounded-lg text-xs bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-1"
                data-testid={`appt-counter-${appt.id}`}>
                <ArrowRightLeft className="w-3.5 h-3.5" /> Autre horaire
              </button>
            )}
            <button onClick={() => onRespond(appt.id, "declined")} disabled={responding}
              className="px-3 py-1.5 rounded-lg text-xs border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid={`appt-decline-${appt.id}`}>
              <XCircle className="w-3.5 h-3.5" /> Refuser
            </button>
          </div>
        )}
      </div>

      {appt.status === "counter_proposed" && appt.original_start_time && (
        <div className="mt-3 bg-violet-50 border border-violet-200 rounded-lg px-3 py-1.5 text-[11px] text-violet-700 flex items-center gap-1.5">
          <ArrowRightLeft className="w-3 h-3" />
          Date initiale proposée : {fmtDate(appt.original_start_time)}
        </div>
      )}

      {appt.status === "pending" && isAdminRequest && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-[11px] text-amber-700 flex items-center gap-1.5">
          <AlertCircle className="w-3 h-3" />
          Merci de confirmer, refuser, ou proposer un autre horaire
        </div>
      )}

      {appt.status === "pending" && !isAdminRequest && (
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 text-[11px] text-blue-700 flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          En attente de réponse du comptable
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Batch #14.151 — Formulaire de demande de RDV par le client
// =====================================================================
function RequestForm({ primaryColor, busy, onClose, onSubmit }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "online",
    preferred_date: "",
    preferred_time: "10:00",
    duration_minutes: 30,
    notes_client: "",
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.preferred_date) {
      alert("Sujet et date requis");
      return;
    }
    onSubmit(form);
  };

  return (
    <form onSubmit={submit} className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 space-y-3"
          data-testid="appt-portal-request-form">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-amber-900 text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Demander un rendez-vous
        </h3>
        <button type="button" onClick={onClose} className="text-amber-700 hover:text-amber-900">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div>
        <label className="block text-[10px] uppercase font-semibold text-amber-700 mb-1">Sujet *</label>
        <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
               placeholder="Ex: Discussion impôts, signature de documents..."
               className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white"
               required data-testid="appt-portal-request-title" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] uppercase font-semibold text-amber-700 mb-1">Date préférée *</label>
          <input type="date" value={form.preferred_date}
                 onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                 min={new Date().toISOString().slice(0, 10)}
                 className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white"
                 required data-testid="appt-portal-request-date" />
        </div>
        <div>
          <label className="block text-[10px] uppercase font-semibold text-amber-700 mb-1">Heure préférée</label>
          <input type="time" value={form.preferred_time}
                 onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                 className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white"
                 data-testid="appt-portal-request-time" />
        </div>
        <div>
          <label className="block text-[10px] uppercase font-semibold text-amber-700 mb-1">Durée</label>
          <select value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white">
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={45}>45 min</option>
            <option value={60}>1 heure</option>
            <option value={90}>1h30</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-[10px] uppercase font-semibold text-amber-700 mb-1">Type de rencontre</label>
        <div className="flex gap-3 text-sm">
          <label className="flex items-center gap-1.5">
            <input type="radio" name="type" value="online" checked={form.type === "online"}
                   onChange={(e) => setForm({ ...form, type: e.target.value })} />
            <Video className="w-3 h-3" /> En ligne
          </label>
          <label className="flex items-center gap-1.5">
            <input type="radio" name="type" value="in_person" checked={form.type === "in_person"}
                   onChange={(e) => setForm({ ...form, type: e.target.value })} />
            <MapPin className="w-3 h-3" /> En personne
          </label>
        </div>
      </div>
      <div>
        <label className="block text-[10px] uppercase font-semibold text-amber-700 mb-1">Détails (optionnel)</label>
        <textarea value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3} placeholder="Précisions sur votre demande..."
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white resize-y" />
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-amber-200">
        <button type="button" onClick={onClose} disabled={busy}
                className="px-3 py-2 rounded-lg text-sm text-amber-700 hover:bg-amber-100 disabled:opacity-50">
          Annuler
        </button>
        <button type="submit" disabled={busy}
                className="px-4 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
                style={{ backgroundColor: primaryColor }}
                data-testid="appt-portal-request-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
          {busy ? "Envoi..." : "Envoyer la demande"}
        </button>
      </div>
    </form>
  );
}

// =====================================================================
// Batch #14.151 — Formulaire de contre-proposition
// =====================================================================
function CounterForm({ primaryColor, appt, busy, onClose, onSubmit }) {
  const [date, setDate] = useState(appt.start_time?.slice(0, 10) || "");
  const [time, setTime] = useState(appt.start_time?.slice(11, 16) || "10:00");
  const [duration, setDuration] = useState(30);
  const [note, setNote] = useState("");

  const submit = () => {
    if (!date || !time) { alert("Date et heure requises"); return; }
    onSubmit(`${date}T${time}:00`, duration, note);
  };

  return (
    <div className="mt-2 bg-violet-50 border-2 border-violet-200 rounded-xl p-3 space-y-2"
         data-testid={`appt-portal-counter-form-${appt.id}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold text-violet-900 flex items-center gap-1">
          <ArrowRightLeft className="w-3 h-3" /> Proposer un autre horaire
        </h4>
        <button onClick={onClose} className="text-violet-700 hover:text-violet-900">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <label className="block text-[10px] uppercase font-semibold text-violet-700 mb-0.5">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 min={new Date().toISOString().slice(0, 10)}
                 className="w-full px-2 py-1.5 border border-violet-200 rounded text-sm bg-white" />
        </div>
        <div>
          <label className="block text-[10px] uppercase font-semibold text-violet-700 mb-0.5">Heure</label>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                 className="w-full px-2 py-1.5 border border-violet-200 rounded text-sm bg-white" />
        </div>
        <div>
          <label className="block text-[10px] uppercase font-semibold text-violet-700 mb-0.5">Durée</label>
          <select value={duration} onChange={(e) => setDuration(e.target.value)}
                  className="w-full px-2 py-1.5 border border-violet-200 rounded text-sm bg-white">
            <option value={15}>15 min</option><option value={30}>30 min</option>
            <option value={45}>45 min</option><option value={60}>1 heure</option>
          </select>
        </div>
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Note (optionnel) : pourquoi proposez-vous cet horaire ?"
                className="w-full px-2 py-1.5 border border-violet-200 rounded text-xs bg-white resize-y" />
      <div className="flex justify-end gap-2">
        <button onClick={onClose} disabled={busy}
                className="px-2 py-1 text-xs text-violet-700 hover:bg-violet-100 rounded">
          Annuler
        </button>
        <button onClick={submit} disabled={busy}
                className="px-3 py-1 rounded text-white text-xs font-medium hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
                style={{ backgroundColor: primaryColor }}
                data-testid={`appt-portal-counter-submit-${appt.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
          Envoyer
        </button>
      </div>
    </div>
  );
}
